import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom, forkJoin, Subject } from 'rxjs';
import { debounceTime, delay, takeUntil } from 'rxjs/operators';
import * as FileSaver from 'file-saver';
import moment from 'moment';
import { MasterModel } from '../../../../master/models/master.model';
import { MasterConceptModel } from '../../../../master/models/master-concept.model';
import { MasterService } from '../../../../master/services/master.service';
import { BaseFormComponent } from '../../../../shared/components/base/base-form.component';
import { TicketLookupsFacade } from '../../models/ticket-lookups.facade';
import { TicketFormFactory } from '../../models/ticket-form.factory';
import { ActivityService } from '../../../../../modules/user/activity/activity.service';
import { UserCompanyService } from '../../../../../modules/user/usercompany/usercompany.service';
import { ContactService } from '../../../../../modules/contact/contact/contact.service';
import { ConceptService } from '../../../../../modules/concept/concept/concept.service';
import { ConceptSerialNumberComponent } from '../../../../../modules/concept/concept/serialNumber/concept-serialNumber.component';
import { ContactFormV2Component } from "../../../../contacts/components/contact-form-v2/contact-form-v2.component";
import { TicketService as OldTicketService } from '../../../../../modules/invoice/tickets/ticket.service';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { ActualSelected, Utils } from '../../../../../shared/utils';

const DEFAULT_CONTACT = 'Factura simplificada';
const CASH_PAYMENT = 'Efectivo';
const PARTNER_PAYMENT = 'Socio';
const BANK_PAYMENTS = ['Transferencia bancaria', 'Remesa en cuenta', 'Tarjeta', 'Confirming', 'Pagarés', 'Bizum'];
const SALES_ACCOUNT = '7000000001';
const SERVICES_ACCOUNT = '7050000001';

@Component({
  selector: 'app-ticket-form',
  templateUrl: './ticket-form.components.html',
  styleUrls: ['./ticket-form.components.css'],
  standalone: false
})
export class TicketFormV2Component extends BaseFormComponent implements OnInit, OnDestroy {
  @Input() mode: 'page' | 'modal' | 'panel' = 'page';

  form: UntypedFormGroup;
  isSaving = false;
  isLoading = true;
  submitted = false;
  isProjectEnable = false;
  nextTicketNumber = '';

  contacts: MasterModel[] = [];
  payMethods: MasterModel[] = [];
  bankAccounts: MasterModel[] = [];
  cashRegisters: MasterModel[] = [];
  partners: MasterModel[] = [];
  taxes: MasterModel[] = [];
  concepts: MasterModel[] = [];
  projects: MasterModel[] = [];
  accountingModels: MasterModel[] = [];

  subtotal = 0;
  taxesTotal = 0;
  retentionsTotal = 0;
  total = 0;
  taxesLabel = 'Impuestos';
  lineTotals: number[] = [];

  private readonly CHUNK = 30;
  visibleCount = this.CHUNK;

  private taxById = new Map<any, any>();
  private contactTaxUids: string[] = [];
  private readonly lazyLookups = new Map<string, Promise<void>>();
  private lastValidTaxesByLineKey: any = {};
  private prevTaxesByLineKey: any = {};
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private masterService: MasterService,
    private lookupsFacade: TicketLookupsFacade,
    private activityService: ActivityService,
    private userCompanyService: UserCompanyService,
    private contactService: ContactService,
    private conceptService: ConceptService,
    private oldTicketService: OldTicketService,
    private loading: LoadingDialogService,
    protected snackBar: MatSnackBar,
    protected dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<TicketFormV2Component>
  ) {
    super(dialog, snackBar);

    if (data && data.mode) this.mode = data.mode;
    this.form = data && data.form ? data.form : TicketFormFactory.buildForm(this.fb);
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get lines(): UntypedFormArray {
    return TicketFormFactory.lines(this.form);
  }

  get visibleLines(): any[] {
    return this.lines.controls.slice(0, this.visibleCount);
  }

  onLinesScroll(ev: any): void {
    const el = ev && ev.target;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200 && this.visibleCount < this.lines.length) {
      this.visibleCount = Math.min(this.visibleCount + this.CHUNK, this.lines.length);
    }
  }

  private async load(): Promise<void> {
    this.isLoading = true;
    try {
      const [lookups, activity, userCompany] = await Promise.all([
        firstValueFrom(forkJoin({
          contacts: this.masterService.getContacts(),
          payMethods: this.masterService.getPayMethodTickets(),
          taxes: this.masterService.getTaxes(),
          concepts: this.masterService.getConcepts(),
          accountingModels: this.masterService.getAccountingModels(),
        })),
        this.activityService.loadData(ActualSelected.activitySelected),
        this.userCompanyService.loadData().catch(() => null),
      ]);

      this.contacts = lookups.contacts || [];
      this.payMethods = lookups.payMethods || [];
      this.taxes = lookups.taxes || [];
      this.concepts = lookups.concepts || [];
      this.accountingModels = lookups.accountingModels || [];
      this.buildTaxIndex();

      this.nextTicketNumber = activity && activity.ticketNumber ? activity.ticketNumber : '';
      this.isProjectEnable = !!(activity && activity.isProject);
      if (this.isProjectEnable) await this.ensureProjects();

      this.applyDefaults(activity, userCompany);
      await this.ensurePaymentLookups(this.payLabel());
      this.applyDefaultCashRegister();

      this.wireForm();
      if (this.lines.length === 0) this.addLine();
      this.applyPaymentValidators();
      this.recomputeTotals();

      this.isLoading = false;
      this.resolveProvidedContact();
    } catch {
      this.isLoading = false;
      this.notification.errorBottom('Error cargando datos del formulario.');
    }
  }

  private applyDefaults(activity: any, userCompany: any): void {
    const contactCtrl = this.form.get('contactUid');
    if (!contactCtrl.value) {
      const contact = this.contacts.find(c => c.display === DEFAULT_CONTACT);
      if (contact) contactCtrl.setValue(contact, { emitEvent: false });
    }

    const payCtrl = this.form.get('payMethodUid');
    if (!payCtrl.value) {
      const payMethod = this.payMethods.find(p => p.display === CASH_PAYMENT);
      if (payMethod) payCtrl.setValue(payMethod, { emitEvent: false });
    }

    const accountCtrl = this.form.get('entranceAccount');
    if (!accountCtrl.value) {
      const number = userCompany && userCompany.isSalesInvoice ? SALES_ACCOUNT : userCompany && userCompany.isServiceInvoice ? SERVICES_ACCOUNT : null;
      const account = this.accountingModels.find(a => this.accountNumber(a) === number)
        || this.accountingModels.find(a => activity && activity.ticketAccountEntrance && (a.display === activity.ticketAccountEntrance || this.accountNumber(a) === activity.ticketAccountEntrance))
        || this.accountingModels.find(a => this.accountNumber(a) === SERVICES_ACCOUNT);
      if (account) accountCtrl.setValue(account, { emitEvent: false });
    }
  }

  private applyDefaultCashRegister(): void {
    const cashCtrl = this.form.get('cashRegisterUid');
    if (this.isCashRegisterPayment() && !cashCtrl.value && this.cashRegisters.length > 0) {
      cashCtrl.setValue(this.cashRegisters[0], { emitEvent: false });
    }
  }

  private resolveProvidedContact(): void {
    if (!this.data || !this.data.form) return;
    const value = this.form.get('contactUid').value;
    if (!value || value.display === DEFAULT_CONTACT) return;

    if (value.id && this.contacts.some(c => this.sameId(c.id, value.id))) {
      this.assingDataContact();
    } else if (value.display || value.id) {
      this.onContactChange({ id: value.display || value.id, display: value.display || value.id });
    }
  }

  private wireForm(): void {
    this.form.get('paid').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyPaymentValidators());

    this.form.get('payMethodUid').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyPaymentValidators();
      this.ensurePaymentLookups(this.payLabel()).then(() => this.applyDefaultCashRegister());
    });

    this.form.valueChanges
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => this.recomputeTotals());
  }

  private applyPaymentValidators(): void {
    const paid = this.form.get('paid').value === true;
    const rules: { [key: string]: boolean } = {
      payMethodUid: paid,
      date: paid,
      cashRegisterUid: paid && this.isCashRegisterPayment(),
      bankAccountUid: paid && this.isBankAccountPayment(),
      partnerUid: paid && this.isPartnerPayment(),
    };

    Object.keys(rules).forEach(key => {
      const ctrl = this.form.get(key);
      if (rules[key]) ctrl.setValidators(Validators.required);
      else ctrl.clearValidators();
      ctrl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private ensureLookup(key: string, load: () => Promise<void>): Promise<void> {
    let entry = this.lazyLookups.get(key);
    if (!entry) {
      entry = load().catch(() => {
        this.lazyLookups.delete(key);
      });
      this.lazyLookups.set(key, entry);
    }
    return entry;
  }

  private ensurePaymentLookups(label: string): Promise<void> {
    const tasks: Promise<void>[] = [];
    if (label === CASH_PAYMENT) {
      tasks.push(this.ensureLookup('cashRegisters', () => firstValueFrom(this.masterService.getCashRegisters()).then(list => { this.cashRegisters = list || []; })));
    }
    if (BANK_PAYMENTS.indexOf(label) !== -1) {
      tasks.push(this.ensureLookup('bankAccounts', () => firstValueFrom(this.masterService.getBankAccounts()).then(list => { this.bankAccounts = list || []; })));
    }
    if (label === PARTNER_PAYMENT) {
      tasks.push(this.ensureLookup('partners', () => firstValueFrom(this.masterService.getParnerts()).then(list => { this.partners = list || []; })));
    }
    return Promise.all(tasks).then(() => undefined);
  }

  private ensureProjects(): Promise<void> {
    return this.ensureLookup('projects', () => firstValueFrom(this.masterService.getProjects()).then(list => { this.projects = list || []; }));
  }

  private payLabel(): string {
    const value = this.form.get('payMethodUid').value;
    return value && value.display ? value.display : '';
  }

  isCashRegisterPayment(): boolean {
    return this.payLabel() === CASH_PAYMENT;
  }

  isBankAccountPayment(): boolean {
    return BANK_PAYMENTS.indexOf(this.payLabel()) !== -1;
  }

  isPartnerPayment(): boolean {
    return this.payLabel() === PARTNER_PAYMENT;
  }

  addLine(): void {
    this.lines.push(TicketFormFactory.createLine(this.fb));
    this.visibleCount = this.lines.length;
    this.recomputeTotals();
  }

  removeLine(index: number): void {
    this.lines.removeAt(index);
    this.recomputeTotals();
  }

  dropLine(event: any): void {
    const moved = this.lines.at(event.previousIndex);
    this.lines.removeAt(event.previousIndex);
    this.lines.insert(event.currentIndex, moved);
    this.recomputeTotals();
  }

  onConceptChange(line: any): void {
    const value = line.get('conceptUid').value;
    const concept = value ? this.concepts.find(c => this.sameId(c.id, value.id)) as MasterConceptModel : null;

    if (!concept) {
      const contactTaxes = this.contactDefaultTaxes();
      if (contactTaxes.length > 0) line.get('tax').setValue(contactTaxes);
      return;
    }

    const metadata: any = concept.metadata || {};
    line.get('quantity').setValue(1);
    line.get('price').setValue(metadata.netPrice || 0);
    line.get('discount').setValue(metadata.discount || 0);

    const conceptTaxes = (metadata.taxes || [])
      .map((t: any) => this.taxes.find(tax => tax.metadata && tax.metadata.id == t.taxId))
      .filter(Boolean);
    line.get('tax').setValue(conceptTaxes.length > 0 ? conceptTaxes : this.contactDefaultTaxes());

    if (metadata.serialNumber) {
      this.conceptService.loadData(concept.id).then(response => {
        this.dialog.open(ConceptSerialNumberComponent, {
          width: '640px',
          maxWidth: '96vw',
          height: '70%',
          data: { concept: response, live: true }
        }).afterClosed().subscribe(result => {
          if (!result) return;
          const current = line.get('conceptUid').value;
          line.get('conceptUid').setValue({ ...current, display: current.display + ' - S/N: ' + result });
        });
      });
    }
  }

  private contactDefaultTaxes(): any[] {
    return this.contactTaxUids
      .map(uid => this.taxes.find(t => this.sameId(t.id, uid)))
      .filter(Boolean);
  }

  onContactChange(selectedValue: any): void {
    if (!selectedValue) return;

    if (this.contacts.some(c => this.sameId(c.id, selectedValue.id))) {
      this.assingDataContact();
      return;
    }

    ContactFormV2Component.open(this.dialog, { scratch: { companyName: selectedValue.id, isClient: true } })
      .pipe(delay(1200))
      .subscribe(result => {
        this.lookupsFacade.loadContacts$()
          .pipe(takeUntil(this.destroy$))
          .subscribe((list: any[]) => {
            this.contacts = list || [];
            const contact = this.contacts.find(c => this.sameId(c.id, result && result.id))
              || this.contacts.find(c => c.display === selectedValue.id);
            if (result && contact) {
              this.form.get('contactUid').setValue(contact);
              this.assingDataContact();
            }
          });
      });
  }

  assingDataContact(): void {
    const contactValue = this.form.get('contactUid').value;
    if (!contactValue || !contactValue.id) return;

    const request = this.contactService.loadData(contactValue.id)
      .then(response => this.ensurePaymentLookups(response ? response.paymentTypeClient : '').then(() => response));

    this.loading.run('Cargando datos del contacto…', request).then(response => {
      if (!response) return;

      this.contactTaxUids = [response.taxClientUid, response.taxClientSecondUid].filter(Boolean);

      const payMethod = this.payMethods.find(p => p.display === response.paymentTypeClient);
      if (payMethod) this.form.get('payMethodUid').setValue(payMethod);

      if (response.paymentClientUid != null && this.isBankAccountPayment()) {
        const bank = this.bankAccounts.find(b => this.sameId(b.id, response.paymentClientUid));
        if (bank) this.form.get('bankAccountUid').setValue(bank);
      }
      if (response.clientEfectivoId != null && this.isCashRegisterPayment()) {
        const cash = this.cashRegisters.find(c => c.metadata && c.metadata.id == response.clientEfectivoId);
        if (cash) this.form.get('cashRegisterUid').setValue(cash);
      }
      if (response.clientPartnersId != null && this.isPartnerPayment()) {
        const partner = this.partners.find(p => p.metadata && String(p.metadata.id) === String(response.clientPartnersId));
        if (partner) this.form.get('partnerUid').setValue(partner);
      }
    }).catch(() => this.notification.errorBottom('No se pudieron cargar los datos del contacto.'));
  }

  onSave(): void {
    if (this.isSaving) return;

    this.submitted = true;
    this.applyPaymentValidators();
    this.form.markAllAsTouched();
    this.lines.controls.forEach(line => {
      const control = line.get('conceptUid');
      if (control.value && !this.lineProduct(control.value)) control.setErrors({ required: true });
    });

    if (this.form.invalid || this.lines.length === 0) {
      this.showValidationErrors();
      return;
    }

    this.recomputeTotals();
    const value = this.form.getRawValue();
    const contact = this.contacts.find(c => this.sameId(c.id, value.contactUid && value.contactUid.id));
    if (!contact) {
      this.notification.errorBottom('Selecciona un contacto de la lista.');
      return;
    }

    const paid = value.paid === true;
    const payMethod = paid ? this.payMethods.find(p => this.sameId(p.id, value.payMethodUid && value.payMethodUid.id)) : null;
    if (paid && !payMethod) {
      this.notification.errorBottom('Selecciona un tipo de cobro de la lista.');
      return;
    }

    const cash = paid && this.isCashRegisterPayment() ? this.cashRegisters.find(c => this.sameId(c.id, value.cashRegisterUid && value.cashRegisterUid.id)) : null;
    const bank = paid && this.isBankAccountPayment() ? this.bankAccounts.find(b => this.sameId(b.id, value.bankAccountUid && value.bankAccountUid.id)) : null;
    const partner = paid && this.isPartnerPayment() ? this.partners.find(p => this.sameId(p.id, value.partnerUid && value.partnerUid.id)) : null;
    const project = this.isProjectEnable ? this.projects.find(p => this.sameId(p.id, value.projectUid && value.projectUid.id)) : null;
    const account = this.accountingModels.find(a => this.sameId(a.id, value.entranceAccount && value.entranceAccount.id));

    const command: any = {
      rectifying: false,
      companyName: '',
      vatNumber: '',
      address: '',
      location: '',
      postalCode: '',
      state: '',
      country: '',
      total: this.total,
      entranceAccount: account ? account.display : (value.entranceAccount && value.entranceAccount.display) || null,
      expenseAccount: null,
      receiverName: contact.display || DEFAULT_CONTACT,
      receiverUid: contact.id,
      projectUid: project ? project.id : null,
      typePay: payMethod ? payMethod.display : null,
      paided: paid ? moment(value.date).format('YYYY-MM-DD') : null,
      efectivoId: cash && cash.metadata ? cash.metadata.id : null,
      bankAccountId: bank ? bank.id : null,
      partnersId: partner ? Number(partner.id) || 0 : 0,
      fiscalYear: Number(ActualSelected.yearSelected),
      activityUid: ActualSelected.activitySelected,
      ticketLines: this.buildLines(value.lines),
    };

    const request = this.oldTicketService.addData(command)
      .then(ticket => this.oldTicketService.pdf(ticket.uid)
        .then(blob => ({ ticket, blob }))
        .catch(() => ({ ticket, blob: null as Blob })));

    this.isSaving = true;
    this.loading.run('Guardando factura simplificada…', request)
      .then(({ ticket, blob }) => {
        if (blob) {
          FileSaver.saveAs(blob, Utils.GetPdfName('Ticket_' + (ticket.ticketNumber || this.nextTicketNumber)));
          this.notification.okBottom('Factura simplificada guardada correctamente');
        } else {
          this.notification.warningBottom('Factura simplificada guardada, pero no se pudo descargar el PDF.');
        }
        this.finish(true);
      })
      .catch(error => {
        this.notification.errorBottom(error && error.error && error.error.message ? error.error.message : 'Error al guardar la factura simplificada.');
      })
      .finally(() => this.isSaving = false);
  }

  private buildLines(lines: any[]): any[] {
    return (lines || []).map((line, index) => {
      const concept = this.concepts.find(c => this.sameId(c.id, line.conceptUid && line.conceptUid.id));
      const unitPrice = Number(line.price) || 0;
      const quantity = Number(line.quantity) || 0;
      const discount = Number(line.discount) || 0;
      const base = unitPrice * quantity * (1 - discount / 100);

      const taxes = (line.tax || [])
        .map((t: any) => this.findTaxFull(t))
        .filter(Boolean)
        .map((tax: any) => ({
          uid: tax.id,
          name: tax.display || '',
          taxImport: tax.metadata && tax.metadata.import != null ? Number(tax.metadata.import) : 0,
          type: tax.metadata && tax.metadata.type ? tax.metadata.type : 'IVA',
          taxId: tax.metadata && tax.metadata.id ? tax.metadata.id : 0,
        }));

      const taxAmount = taxes.reduce((sum: number, tax: any) => sum + base * (tax.taxImport / 100), 0);

      return {
        product: this.lineProduct(line.conceptUid) || (concept ? concept.display : ''),
        conceptUid: concept ? concept.id : null,
        quantity: quantity,
        discount: discount,
        unityAmount: unitPrice,
        position: index,
        totalLine: base + taxAmount,
        taxes: taxes,
      };
    });
  }

  private lineProduct(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    return value.display ? String(value.display).trim() : '';
  }

  private showValidationErrors(): void {
    const messages = TicketFormFactory.getValidationMessages(this.form);
    if (this.lines.length === 0) messages.unshift('Añade al menos una línea de concepto.');

    const shown = messages.slice(0, 6).map(message => '• ' + message);
    const rest = messages.length - shown.length;
    if (rest > 0) shown.push(`• y ${rest} más`);
    this.notification.errorBottom(shown.length ? 'Revisa los campos marcados en rojo:\n' + shown.join('\n') : 'Revisa los campos marcados en rojo.');

    this.visibleCount = this.lines.length;
    setTimeout(() => {
      const firstInvalid = document.querySelector('app-ticket-form .mat-form-field-invalid, app-ticket-form .mat-mdc-form-field-invalid, app-ticket-form .pf-empty-lines--invalid');
      if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  onCancel(): void {
    this.finish(false);
  }

  private finish(saved: boolean): void {
    if (this.dialogRef) {
      this.dialogRef.close(saved);
      return;
    }
    this.router.navigate(['invoice/ticket']);
  }

  recomputeTotals(): void {
    const values = this.lines.value || [];
    let subtotal = 0;
    let taxesTotal = 0;
    let retentionsTotal = 0;
    let hasIVA = false;
    let hasIGIC = false;
    let hasAIEM = false;
    const lineTotals: number[] = [];

    for (const line of values) {
      const base = (Number(line.price) || 0) * (Number(line.quantity) || 0) * (1 - (Number(line.discount) || 0) / 100);
      subtotal += base;
      let lineSum = base;

      for (const selected of line.tax || []) {
        const tax = this.findTaxFull(selected);
        const rate = tax && tax.metadata && tax.metadata.import != null ? Number(tax.metadata.import) : 0;
        const amount = base * (rate / 100);
        lineSum += amount;
        if (rate > 0) taxesTotal += amount;
        else if (rate < 0) retentionsTotal += amount;

        const type = tax && tax.metadata && tax.metadata.type ? String(tax.metadata.type).toUpperCase() : '';
        if (type.indexOf('AIEM') !== -1) hasAIEM = true;
        if (type.indexOf('IGIC') !== -1) hasIGIC = true;
        if (type.indexOf('IVA') !== -1) hasIVA = true;
      }

      lineTotals.push(lineSum);
    }

    this.subtotal = subtotal;
    this.taxesTotal = taxesTotal;
    this.retentionsTotal = retentionsTotal;
    this.total = subtotal + taxesTotal + retentionsTotal;
    this.lineTotals = lineTotals;
    this.taxesLabel = hasIVA ? 'IVA' : hasIGIC ? 'IGIC' : hasAIEM ? 'AIEM' : 'Impuestos';
  }

  private buildTaxIndex(): void {
    this.taxById = new Map<any, any>();
    for (const tax of this.taxes) {
      if (tax && tax.id != null) this.taxById.set(String(tax.id).toLowerCase(), tax);
    }
  }

  private findTaxFull(tx: any): any {
    if (!tx) return null;
    const id = tx.id != null ? tx.id : tx.uid != null ? tx.uid : tx.value;
    if (id == null) return null;
    return this.taxById.get(String(id).toLowerCase()) || null;
  }

  handleTaxFocus = (lineIndex: number): void => {
    const line = this.lines.at(lineIndex);
    if (!line) return;
    this.prevTaxesByLineKey[this.getLineKey(line)] = JSON.parse(JSON.stringify(line.get('tax').value || []));
  }

  handleTaxChange = (value: any, lineIndex: number): string => {
    const line = this.lines.at(lineIndex);
    if (!line) return '';

    const ctrl = line.get('tax');
    const key = this.getLineKey(line);
    const current = ctrl.value || [];

    if (!this.lastValidTaxesByLineKey[key]) {
      this.lastValidTaxesByLineKey[key] = JSON.parse(JSON.stringify(current));
    }

    const validation = this.validateTaxesSelection(current);
    if (!validation.ok) {
      const previous = this.prevTaxesByLineKey[key] || this.lastValidTaxesByLineKey[key];
      ctrl.setValue(JSON.parse(JSON.stringify(previous)), { emitEvent: true });
      if (validation.message) this.notification.errorBottom(validation.message);
      return '';
    }

    this.lastValidTaxesByLineKey[key] = JSON.parse(JSON.stringify(current));
    return '';
  }

  private getLineKey(line: any): string {
    if (!line.__taxKey) {
      line.__taxKey = 'l_' + Math.random().toString(16).slice(2) + '_' + Date.now();
    }
    return String(line.__taxKey);
  }

  private validateTaxesSelection(selected: any[]): { ok: boolean; message: string } {
    const used: any = {};
    const mainTypeUsed: any = {};

    for (const tax of selected || []) {
      if (!tax) continue;
      const id = tax.id != null ? tax.id : tax.uid != null ? tax.uid : tax.value;
      if (id == null) continue;

      if (used[id]) return { ok: false, message: 'Este impuesto ya ha sido aplicado.' };

      const type = tax.type ? String(tax.type) : tax.metadata && tax.metadata.type ? String(tax.metadata.type) : '';
      const name = tax.metadata && tax.metadata.name ? String(tax.metadata.name).toLowerCase() : '';
      const isRecargo = name.indexOf('recargo equiv') >= 0 || name.indexOf('recargo de equiv') >= 0;

      if (!isRecargo && type) {
        if (mainTypeUsed[type]) return { ok: false, message: 'Ya se ha aplicado un impuesto del tipo ' + type + '.' };
        mainTypeUsed[type] = true;
      }

      used[id] = true;
    }

    return { ok: true, message: '' };
  }

  private accountNumber(account: any): string {
    if (!account) return '';
    if (account.metadata && account.metadata.number) return String(account.metadata.number);
    return String(account.display || '').split(' - ')[0].trim();
  }

  private sameId(a: any, b: any): boolean {
    return a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();
  }

  public trackByIndex(i: number) { return i; }
}
